/**
 * Auftrag erteilen.
 *
 * Pflichtfeld Auftragswert mit de-AT-Formatierung während der Eingabe.
 * Enter bestätigt. Erfolgsfall: Toast. Fehlerfall beim Abgleich: kein Toast,
 * sondern das dauerhafte Sync-Banner am Angebot.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { betragFormatieren, betragLesen, gruppiere } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';

export function AuftragModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const [eingabe, setEingabe] = useState(
    angebot.auftrag ? gruppiere(angebot.auftrag) : '',
  );
  const [fehler, setFehler] = useState('');

  const mutation = useMutation({
    mutationFn: (auftrag: number) =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/auftrag`, { auftrag }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function bestaetigen() {
    const wert = betragLesen(eingabe);
    if (!wert || wert <= 0) {
      setFehler('Bitte den Auftragswert eintragen – Pflichtfeld.');
      return;
    }
    mutation.mutate(wert);
  }

  return (
    <Modal
      titel="Auftrag erteilen"
      untertitel={`${angebot.nr}_${angebot.prefix}`}
      breite={470}
      abstandOben={70}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer ton="gruen" onClick={bestaetigen} disabled={mutation.isPending}>
            {mutation.isPending ? 'Wird gespeichert …' : 'Bestätigen'}
          </KnopfPrimaer>
        </>
      }
    >
      <label className="modal-feld">
        <span>Auftragswert · netto, vor Steuern</span>
        <span className={`modal-betrag ${fehler ? 'modal-betrag--fehler' : ''}`}>
          <input
            autoFocus
            value={eingabe}
            placeholder="0"
            inputMode="numeric"
            onChange={(e) => {
              setEingabe(betragFormatieren(e.target.value));
              setFehler('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') bestaetigen();
            }}
          />
          <span>€</span>
        </span>
      </label>

      <div className="modal-hinweis">
        Der Wert fließt in die Gewinnprognose unter Finanzen ein.
      </div>

      <div className="modal-hinweis">
        Nach dem Bestätigen setzt Kora den Monday-Status und verschiebt den SharePoint-Ordner nach
        „Aufträge". Klappt einer der beiden Schritte nicht, bleibt ein Hinweis am Angebot stehen,
        bis er erfolgreich war.
      </div>

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
